// Copyright (C) 2022 TANNER AG

import { useMemo, useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { compare } from "../../../lib/utils";
import { VehicleTopic, VehicleTopics } from "../../../graphql";
import PagePdf from "../pdf/PagePdf";
import PageThemeItem from "./PageThemeItem";
import useTranslation from "../../../hooks/usetranslation";
import useVehiclePdf from "../../../hooks/usevehiclepdf";

type FilterTopic = Pick<VehicleTopic, "id" | "key" | "title" | "totalUsage" | "type" | "relatedTopics" | "relatedSegments">;
type ItemsMap = { benefits: FilterTopic[]; topics: FilterTopic[]; components: FilterTopic[]; segments: FilterTopic[] };
type ItemKey = keyof ItemsMap;

// Filter by segment match: keep items that either have no segment relations
// (universally applicable) or explicitly list the target segment id.
const matchesSegment = (item: FilterTopic, segmentId: string): boolean =>
    !item.relatedSegments?.length || item.relatedSegments.includes(segmentId);

// Strict filter: when activeIds is non-empty, item.id must be in activeIds.
// When activeIds is empty/undefined (active item has no data), include all.
const isActiveRelated = (itemId: string, activeIds?: string[]): boolean =>
    !activeIds?.length || activeIds.includes(itemId);

// Topic relation from item's perspective: item's relatedTopics lists the active id,
// or item has no relatedTopics (universal).
const itemRelatesToTopic = (item: FilterTopic, activeId: string): boolean =>
    !item.relatedTopics?.length || item.relatedTopics.includes(Number(activeId));

// Topic relation from active's perspective: active's relatedTopics lists the item id.
// When active has no relatedTopics, include all.
const activeRelatesToTopic = (itemId: string, activeRelatedTopics?: number[]): boolean =>
    !activeRelatedTopics?.length || activeRelatedTopics.includes(Number(itemId));

// Segment overlap: active and item share any segment, or either side has no segment data.
const hasSegmentOverlap = (item: FilterTopic, activeSegments?: string[]): boolean =>
    !activeSegments?.length ||
    !item.relatedSegments?.length ||
    activeSegments.some((segmentId) => item.relatedSegments?.includes(segmentId));

const filterItems = (items: ItemsMap, activeItem?: FilterTopic): ItemsMap => {
    if (!activeItem) return items;

    const { id, type, relatedSegments, relatedTopics } = activeItem;

    // Define filter predicates for each item type based on active item type
    const filters: Record<string, Partial<Record<ItemKey, (item: FilterTopic) => boolean>>> = {
        segment: {
            components: (item) => matchesSegment(item, id),
            topics: (item) => matchesSegment(item, id),
            benefits: (item) => matchesSegment(item, id)
        },
        component: {
            segments: (item) => isActiveRelated(item.id, relatedSegments),
            topics: (item) => activeRelatesToTopic(item.id, relatedTopics),
            benefits: (item) => hasSegmentOverlap(item, relatedSegments)
        },
        topic: {
            segments: (item) => isActiveRelated(item.id, relatedSegments),
            components: (item) => itemRelatesToTopic(item, id),
            benefits: (item) => hasSegmentOverlap(item, relatedSegments)
        },
        benefit: {
            segments: (item) => isActiveRelated(item.id, relatedSegments),
            components: (item) => hasSegmentOverlap(item, relatedSegments),
            topics: (item) => activeRelatesToTopic(item.id, relatedTopics)
        }
    };

    const typeFilters = filters[type];
    if (!typeFilters) return items;

    return {
        segments: typeFilters.segments ? items.segments.filter(typeFilters.segments) : items.segments,
        components: typeFilters.components ? items.components.filter(typeFilters.components) : items.components,
        topics: typeFilters.topics ? items.topics.filter(typeFilters.topics) : items.topics,
        benefits: typeFilters.benefits ? items.benefits.filter(typeFilters.benefits) : items.benefits
    };
};

type Props = {
    topics?: VehicleTopics;
    isPDFMode: boolean;
};

const PageFeatures: React.FC<Props> = ({ topics: vehicleTopics, isPDFMode }) => {
    const { t } = useTranslation();
    const [activeItem, setActiveItem] = useState<FilterTopic>();
    const { items, itemIds, toggleItem } = useVehiclePdf();

    const silos = useMemo(() => {
        const { topics, benefits, components, segments } = filterItems(
            {
                benefits: vehicleTopics?.benefits ?? [],
                topics: vehicleTopics?.topics ?? [],
                components: vehicleTopics?.components ?? [],
                segments: vehicleTopics?.segments ?? []
            },
            activeItem
        );

        // Show silo when the original category has items, even if filter empties it —
        // preserves column headers during filtering.
        return [
            { label: t("segments"), items: segments, hasSource: !!vehicleTopics?.segments?.length },
            { label: t("benefits"), items: benefits, hasSource: !!vehicleTopics?.benefits?.length },
            { label: t("topics"), items: topics, hasSource: !!vehicleTopics?.topics?.length },
            { label: t("components"), items: components, hasSource: !!vehicleTopics?.components?.length }
        ].filter(({ hasSource }) => hasSource);
    }, [vehicleTopics, activeItem, t]);

    return (
        <>
            <Container sx={{ my: 4 }}>
                <Grid container spacing={8}>
                    {silos.map(({ label, items }, index) => (
                        <Grid key={index} item xs={12} md={4}>
                            <Typography variant="h3" sx={{ mb: 2 }}>
                                {label}
                            </Typography>
                            <Stack spacing={1}>
                                {[...items].sort(compare("title")).map((item) => (
                                    <PageThemeItem
                                        key={item.id}
                                        id={item.id}
                                        to={`../${item.type}/${item.key}`}
                                        name={item.title}
                                        pdf={isPDFMode}
                                        disableNavigation={item.type === "segment"}
                                        onFilterClick={() => {
                                            if (isPDFMode) {
                                                toggleItem(item);
                                            } else {
                                                setActiveItem((currentItem) =>
                                                    currentItem?.id === item.id ? undefined : item
                                                );
                                            }
                                        }}
                                        active={isPDFMode ? itemIds.includes(item.id) : activeItem?.id === item.id}
                                    />
                                ))}
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Container>
            <PagePdf items={items} />
        </>
    );
};

export default PageFeatures;
